/* components/patterns/chat/ChatHeader.jsx — шапка активного чата (S5).
   Название + inline-переименование (карандаш), шеринг private↔workspace (Business+can_manage),
   колокол (плейсхолдер S11), меню «…» (Переименовать/Поделиться/Удалить — не мёртвая кнопка, фикс №17).
   xs: гамбургер → список диалогов; sm: + глобальное меню (useShell).
   Источник: 06 §2.2. ХЕНДОФФ: → frontend/components/chat/ChatHeader.jsx. */

import React from 'react';
import { useShell } from '../../../app/layouts/AppShell.jsx';
import { LearnHubButton } from '../../../app/onboarding/module1/LearnHubButton.jsx';
import { useAuth } from '../../../app/providers/AuthProvider.jsx';
import { useToast } from '../../../app/providers/ToastProvider.jsx';
import { RR } from '../../../lib/router.jsx';
import { NotificationsBell } from '../notifications/NotificationsBell.jsx';
import { SupportButton } from '../SupportButton.jsx';
import { MobileChatsDrawer } from './MobileChatsDrawer.jsx';
import { Button } from '../../ui/Button.jsx';
import { Icon } from '../../ui/Icon.jsx';
import { Popover, PopoverItem } from '../../ui/Popover.jsx';

  const { useState, useEffect, useRef } = React;

  function ChatHeader({ chat, bp, canShare, onRename, onShareToggle, onDelete, onOpenNav }) {
    const toast = useToast();
    const navigate = RR.useNavigate ? RR.useNavigate() : null;
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [chatsOpen, setChatsOpen] = useState(false);
    const inputRef = useRef(null);
    const isMobile = bp === 'xs' || bp === 'sm';
    const shell = useShell ? useShell() : null;

    useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);

    const startEdit = () => { if (!chat) return; setDraft(chat.title); setEditing(true); };
    const commit = () => {
      const v = draft.trim();
      if (v && chat && v !== chat.title) onRename(v);
      setEditing(false);
    };
    const onKey = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      else if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
    };

    const shared = chat && chat.visibility === 'workspace';
    const skillHint = chat && chat.skill_hint;
    // Привязанная сущность (кнопка «Редактировать в чате»): чип под названием,
    // клик открывает сущность в её редакторе Кухни.
    const editTarget = chat && chat.metadata && chat.metadata.edit_target;
    const EDIT_TARGET_LABEL = { ttk: 'ТТК', card: 'Сырьё', menu: 'Меню' };
    const editTargetHref = editTarget
      ? (editTarget.kind === 'card' ? `/kitchen?tab=ingredients&edit=card-${editTarget.id}`
        : editTarget.kind === 'menu' ? `/kitchen?tab=menus&edit=menu-${editTarget.id}`
          : `/kitchen?tab=recipes&edit=ttk-${editTarget.id}`)
      : null;

    return (
      <header className="chat-header">
        {/* xs: иконка чатов слева → полноэкранный список диалогов с поиском
            (на xs нет сайдбара/гамбургера, иначе диалоги недоступны) */}
        {bp === 'xs' && (
          <div className="chat-header__nav">
            <button className="chat-header__icon" onClick={() => setChatsOpen(true)} aria-label="Диалоги">
              <Icon name="chat" size={20} />
            </button>
          </div>
        )}
        {/* sm: гамбургер — открывает единый сайдбар (навигация + диалоги) */}
        {bp === 'sm' && (shell && shell.showHamburger) && (
          <div className="chat-header__nav">
            <button className="chat-header__icon" onClick={onOpenNav || shell.openNav} aria-label="Меню и диалоги">
              <Icon name="menu" size={20} />
            </button>
          </div>
        )}

        <div className="chat-header__titles">
          {editing ? (
            <input ref={inputRef} className="chat-header__rename input" value={draft}
                   onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={onKey}
                   aria-label="Название чата" />
          ) : (
            <div className="chat-header__title-row">
              <h1 className="chat-header__title t-h3 truncate">{chat ? chat.title : 'Ассистент'}</h1>
              {chat && (
                <button className="chat-header__edit" onClick={startEdit} aria-label="Переименовать">
                  <Icon name="edit" size={15} />
                </button>
              )}
              {shared && <span className="badge badge--accent chat-header__shared"><Icon name="share" size={11} /> Общий</span>}
            </div>
          )}
          {skillHint && !editing && <div className="chat-header__sub t-caption text-mute truncate">{skillHint}</div>}
          {editTarget && !editing && (
            <a className="chat-header__target" href={editTargetHref}
               onClick={(e) => { e.preventDefault(); if (navigate) navigate(editTargetHref); }}
               title="Открыть в редакторе">
              <Icon name="link" size={11} />
              <span className="truncate">
                Работаем над: {EDIT_TARGET_LABEL[editTarget.kind] || 'сущность'} «{editTarget.title || '#' + editTarget.id}»
              </span>
            </a>
          )}
        </div>

        <div className="chat-header__actions">
          {/* шеринг — только Business + can_manage, и не на xs (там — в меню «…») */}
          {chat && canShare && bp !== 'xs' && (
            <Button variant="ghost" size="sm" iconLeft="share"
              onClick={() => onShareToggle(!shared)}>
              {shared ? 'В личные' : 'В общие'}
            </Button>
          )}
          {/* «шляпа студента» — хаб обучения (демо-CJM) */}
          <LearnHubButton />
          {/* «Поддержка» — как в PageHeader: правый кластер, не на xs */}
          <SupportButton bp={bp} />
          {/* колокол уведомлений (S11) — единый стор с инбоксом */}
          {NotificationsBell && (
            <NotificationsBell bp={bp}
              showWorkspace={!!(useAuth && useAuth().workspaces && useAuth().workspaces.length > 1)} />
          )}
          {/* меню «…» — рабочее (фикс №17) */}
          {chat && (
            <Popover placement="bottom-end" width={208} trigger={
              <button className="chat-header__icon" aria-label="Меню чата"><Icon name="more-vertical" size={18} /></button>
            }>
              {({ close }) => (
                <div className="stack stack-2">
                  <PopoverItem icon="edit" close={close} onClick={startEdit}>Переименовать</PopoverItem>
                  {canShare && (
                    <PopoverItem icon="share" close={close} onClick={() => onShareToggle(!shared)}>
                      {shared ? 'Вернуть в личные' : 'Вывести в общие'}
                    </PopoverItem>
                  )}
                  <div className="popover__sep" />
                  <PopoverItem icon="trash" danger close={close} onClick={onDelete}>Удалить чат</PopoverItem>
                </div>
              )}
            </Popover>
          )}
        </div>

        {/* полноэкранный список диалогов (xs) */}
        <MobileChatsDrawer open={chatsOpen} onClose={() => setChatsOpen(false)} />
      </header>
    );
  }

export { ChatHeader };
